// Sidebar for the library page

import React from 'react';
import { Link, NavLink } from 'react-router';
import {
  Library,
  Globe,
  HelpCircleIcon,
  MailIcon,
  MessageCircleIcon,
  Balloon,
  DoorOpen,
  MessageSquareLock,
  BotMessageSquare,
  Trophy,
} from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from '../../components/ui/sidebar';
import { AccountFooter } from './account';
import VolumeControlsMenu from './audio/VolumeControlsMenu';
import ContentStyleControl from './contentStyle/ContentStyleControl';
import LoonSummary from './loons/LoonSummary';
import SidebarPanelItem from './SidebarPanelItem';
import PlayerAccessMenu from './moderation/PlayerAccessMenu';
import CensorshipMenu from './moderation/CensorshipMenu';
import PoppyChatMenu from './moderation/PoppyChatMenu';
import Logo from '@/components/navigation/Logo';
import { analyticsService } from '../../services/analyticsService';
import type {
  LibraryModCensorshipController,
  LibraryModPlayerAccessController,
  LibraryPoppyChatController,
} from './hooks';
import { cn } from '@/utilities/cn';
import type { UserAuthFields } from '@airhead-games/shared/types';
import { BRAND_LINKS, buildSupportMailto } from '@airhead-games/shared/configs';
import type { LibraryDialog, LibraryPanel } from './libraryRouteTypes';
import { isWorldUnlocked } from '../world/worldAccess';

type LibrarySidebarProps = {
  user: UserAuthFields | null;
  pathname: string;
  panel: LibraryPanel | null;
  dialog: LibraryDialog | null;
  playerAccess: LibraryModPlayerAccessController;
  censorship: LibraryModCensorshipController;
  poppyChat: LibraryPoppyChatController;
  setPanel: (panel: LibraryPanel | null) => void;
  openDialog: (dialog: LibraryDialog) => void;
};

const LibrarySidebar: React.FC<LibrarySidebarProps> = ({
  user,
  pathname,
  panel,
  dialog,
  playerAccess,
  censorship,
  poppyChat,
  setPanel,
  openDialog,
}) => {
  const { isMobile, state } = useSidebar();
  const collapsedDesktop = !isMobile && state === 'collapsed';
  const compactButtonClassName = collapsedDesktop ? 'justify-center px-2' : '';
  const playerAccessOpen = dialog === null && panel === 'player-access';
  const censorshipOpen = dialog === null && panel === 'censorship';
  const poppyChatOpen = dialog === null && panel === 'poppy-chat';
  const sourcePath = pathname;

  const trackSidebarItemClick = (
    label: string,
    target: string,
    linkType: 'internal' | 'external' = 'internal'
  ) => {
    analyticsService.trackSidebarItemClick(label, {
      surface: 'library_sidebar',
      link_location: 'library_sidebar',
      navigation_target: target,
      link_type: linkType,
      source_path: sourcePath,
      is_mobile: isMobile,
    });
  };

  const handlePanelOpenChange = (
    panelName: string,
    panelId: LibraryPanel,
    nextOpen: boolean
  ) => {
    analyticsService.trackPanelToggle(panelName, nextOpen ? 'open' : 'closed', {
      surface: 'library_sidebar',
      link_location: 'library_sidebar',
      source_path: sourcePath,
      is_mobile: isMobile,
    });
    setPanel(nextOpen ? panelId : null);
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="flex flex-col gap-3 bg-sidebar">
        <div
          className={cn(
            'flex items-center gap-2',
            collapsedDesktop && 'justify-center',
            !collapsedDesktop && isMobile && 'justify-start',
            !collapsedDesktop && !isMobile && 'justify-between'
          )}
        >
          {!collapsedDesktop && <Logo variant="sidebar" />}
          <div
            className={cn(
              'hidden md:flex',
              collapsedDesktop && 'w-full justify-center'
            )}
          >
            <SidebarTrigger />
          </div>
        </div>
        {user && <LoonSummary user={user} openDialog={openDialog} />}
      </SidebarHeader>

      <SidebarContent className="bg-sidebar">
        <SidebarGroup>
          <SidebarGroupLabel className={collapsedDesktop ? 'sr-only' : ''}>
            Play
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={pathname === '/library'}
                  className={compactButtonClassName}
                >
                  <NavLink
                    to="/library"
                    end
                    title="Game Library"
                    onClick={() =>
                      trackSidebarItemClick('Game Library', '/library')
                    }
                  >
                    <Library />
                    {!collapsedDesktop && <span>Game Library</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {isWorldUnlocked(user) && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={pathname === '/world'}
                    className={compactButtonClassName}
                  >
                    <NavLink
                      to="/world"
                      title="World"
                      onClick={() => trackSidebarItemClick('World', '/world')}
                    >
                      <Globe />
                      {!collapsedDesktop && <span>World</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={pathname === '/library/achievements'}
                  className={compactButtonClassName}
                >
                  <NavLink
                    to="/library/achievements"
                    title="Achievements"
                    onClick={() =>
                      trackSidebarItemClick(
                        'Achievements',
                        '/library/achievements'
                      )
                    }
                  >
                    <Trophy />
                    {!collapsedDesktop && <span>Achievements</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Host Style (a page) and Content Style (a panel) keep separate
            folders because players see them as separate things. */}
        <SidebarGroup>
          <SidebarGroupLabel className={collapsedDesktop ? 'sr-only' : ''}>
            Style
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={pathname === '/library/poppy'}
                  className={compactButtonClassName}
                >
                  <NavLink
                    to="/library/poppy"
                    title="Host Style"
                    onClick={() =>
                      trackSidebarItemClick('Host Style', '/library/poppy')
                    }
                  >
                    <Balloon />
                    {!collapsedDesktop && <span>Host Style</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <ContentStyleControl
                  user={user}
                  open={panel === 'content-style'}
                  onOpenChange={(nextOpen) =>
                    handlePanelOpenChange(
                      'Content Style',
                      'content-style',
                      nextOpen
                    )
                  }
                />
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className={collapsedDesktop ? 'sr-only' : ''}>
            Moderation
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarPanelItem
                  isMobile={isMobile}
                  collapsedDesktop={collapsedDesktop}
                  open={playerAccessOpen}
                  onOpenChange={(nextOpen) =>
                    handlePanelOpenChange(
                      'Player Access',
                      'player-access',
                      nextOpen
                    )
                  }
                  title="Player Access"
                  icon={<DoorOpen />}
                  label="Player Access"
                  isActive={playerAccessOpen}
                >
                  <PlayerAccessMenu viewModel={playerAccess.viewModel} />
                </SidebarPanelItem>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarPanelItem
                  isMobile={isMobile}
                  collapsedDesktop={collapsedDesktop}
                  open={censorshipOpen}
                  onOpenChange={(nextOpen) =>
                    handlePanelOpenChange('Censorship', 'censorship', nextOpen)
                  }
                  title="Censorship"
                  icon={<MessageSquareLock />}
                  label="Censorship"
                  isActive={censorshipOpen}
                >
                  <CensorshipMenu viewModel={censorship.viewModel} />
                </SidebarPanelItem>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarPanelItem
                  isMobile={isMobile}
                  collapsedDesktop={collapsedDesktop}
                  open={poppyChatOpen}
                  onOpenChange={(nextOpen) =>
                    handlePanelOpenChange('Twitch Chat', 'poppy-chat', nextOpen)
                  }
                  title="Twitch Chat"
                  icon={<BotMessageSquare />}
                  label="Poppy in Twitch Chat"
                  isActive={poppyChatOpen}
                >
                  <PoppyChatMenu viewModel={poppyChat.viewModel} />
                </SidebarPanelItem>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className={collapsedDesktop ? 'sr-only' : ''}>
            Audio
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <VolumeControlsMenu
                  user={user}
                  open={panel === 'volume-controls'}
                  onOpenChange={(nextOpen) =>
                    handlePanelOpenChange(
                      'Volume Controls',
                      'volume-controls',
                      nextOpen
                    )
                  }
                />
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className={collapsedDesktop ? 'sr-only' : ''}>
            Support
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild className={compactButtonClassName}>
                  <a
                    href={BRAND_LINKS.discord}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Poppy's Pals Discord"
                    onClick={() => {
                      analyticsService.trackButtonClick(
                        "Poppy's Pals Discord",
                        {
                          link_location: 'library_support',
                          link_type: 'external',
                        }
                      );
                      trackSidebarItemClick(
                        "Poppy's Pals Discord",
                        BRAND_LINKS.discord,
                        'external'
                      );
                    }}
                  >
                    <MessageCircleIcon />
                    {!collapsedDesktop && (
                      <span>Poppy&apos;s Pals Discord</span>
                    )}
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild className={compactButtonClassName}>
                  <a
                    href={buildSupportMailto('Support Request')}
                    title="Email Support"
                    onClick={() => {
                      analyticsService.trackButtonClick('Email Support', {
                        link_location: 'library_support',
                      });
                      trackSidebarItemClick(
                        'Email Support',
                        buildSupportMailto('Support Request'),
                        'external'
                      );
                    }}
                  >
                    <MailIcon />
                    {!collapsedDesktop && <span>Email</span>}
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild className={compactButtonClassName}>
                  <Link
                    to="/faqs"
                    title="Frequently Asked Questions"
                    onClick={() =>
                      trackSidebarItemClick(
                        'Frequently Asked Questions',
                        '/faqs'
                      )
                    }
                  >
                    <HelpCircleIcon />
                    {!collapsedDesktop && <span>FAQs</span>}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="bg-sidebar">
        <AccountFooter user={user} openDialog={openDialog} />
      </SidebarFooter>
    </Sidebar>
  );
};

export default LibrarySidebar;
